import { Add, ArrowDownward, ArrowUpward, Article, Close, NavigateBefore, NavigateNext, Preview, RadioButtonChecked, Redo, Splitscreen, Title, Undo, Upload } from "@mui/icons-material";
import { Box, Button, Divider, IconButton, ListSubheader, Menu, MenuItem, Paper, Stack, Tooltip } from "@mui/material";
import { Assessment, createItemByType, Item, newSubHeader, ObjectiveQuestion, TheoryQuestion } from "../../../../../models/assessment";
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "../../../../../components/snackbar";
import SubHeader from "../form/sub-header";
import TheoryView from "../form/theory-view";
import ObjectiveView from "../form/objective-view";
import FileUploadView from "../form/file-upload-view";
import { useClassroomActions } from "../../../../../hooks/useClassroomActions";

type Question = Item | TheoryQuestion | ObjectiveQuestion;

interface BaseProps {
  sectionKey: number;
  setSaved: React.Dispatch<React.SetStateAction<boolean>>;
  saveFunction: () => void;
  register: (id: string, getItem: () => Item) => void;
}

interface ItemsInterface {
    setSaved: React.Dispatch<React.SetStateAction<boolean>>;
    assessment: Assessment;
}

function Items({ setSaved, assessment }: ItemsInterface) {
    
    const itemRefs = useRef<Record<string, () => Item | TheoryQuestion | ObjectiveQuestion>>({});

    const [locAssessment, setLocAssessment] = useState(assessment);
    
    const [currentSection, setCurrentSection] = useState(0);

    const { updateAssessment } = useClassroomActions();

    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);


    const autoSaveTimeout = useRef<NodeJS.Timeout | null>(null);

    const isMenuOpen = Boolean(menuAnchorEl);

    const [history, setHistory] = useState<Assessment[]>([]);
    const [historyIndex, setHistoryIndex] = useState(0);
    const isUndoRedoAction = useRef(false);
    const MAX_HISTORY_LENGTH = 50;


    const { showMessage } = useSnackbar();

    const menuStructure = useMemo(() => {
        return [{
            category: "Header",
            icon: <Title fontSize="small" />,
            items: [
                { name: "Sub Header", type: 0 }
            ]
            },
            {
            category: "Objective",
            icon: <RadioButtonChecked fontSize="small" />,
            items: [
                { name: "Radio", type: 3 },
                { name: "Checkbox", type: 4 }
            ]
            },
            {
            category: "Theory",
            icon: <Article fontSize="small" />,
            items: [
                { name: "Short Text", type: 1 },
                { name: "Paragraph", type: 2 }
            ]
            },
            {
            category: "Universal",
            icon: <Upload fontSize="small" />,
            items: [
                { name: "File Upload", type: 5 }
            ]
        }];
    }, []);
    
    

    const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchorEl(event.currentTarget);
    }, []);
        
    const handleMenuClose = useCallback(() => {
        setMenuAnchorEl(null);
    }, []);  

    const debouncedAutoSave = (ass: Assessment) => {
        if (autoSaveTimeout.current) clearTimeout(autoSaveTimeout.current);

        autoSaveTimeout.current = setTimeout( async () => {
            
            const updatedQuestions: Item[][] = [];

            for (let sectionKey = 0; sectionKey < ass.questions.length; sectionKey++) {
                let sectionQuestions = [];
                
                for (const item of ass.questions[sectionKey]) {
                    if (itemRefs.current[item.id]) {
                        sectionQuestions.push(itemRefs.current[item.id]())
                    }
                    else {
                        sectionQuestions.push(item)
                    }
                }
                
                updatedQuestions.push(sectionQuestions);
            }
            
            const updatedAssessment = {
                ...assessment,
                questions: updatedQuestions
            };

            await updateAssessment(assessment.classroom_id, updatedAssessment);

            setSaved(true);
        }, 3000);
    };

    const itemSave = () => {
        debouncedAutoSave(locAssessment);
    }

    const addToHistory = () => {
        setSaved(false);
        if (isUndoRedoAction.current) {
            isUndoRedoAction.current = false;
            return;
        }
        
        const stateCopy = JSON.parse(JSON.stringify(locAssessment));
        
        const newHistory = history.slice(0, historyIndex + 1);
        
        let updatedHistory = [...newHistory, stateCopy];
        if (updatedHistory.length > MAX_HISTORY_LENGTH) {
            updatedHistory = updatedHistory.slice(updatedHistory.length - MAX_HISTORY_LENGTH);
        }
        
        setHistory(updatedHistory);
        setHistoryIndex(updatedHistory.length);
    };

    const addNewItem = (type: number) => {
        const newItem: Item = {
            ...createItemByType(type),
            'marks': locAssessment.default_marks
        };

            
        const updatedAssessment = {
            ...locAssessment,
            questions: locAssessment.questions.map((section, index) =>
                index === currentSection
                    ? [...section, newItem]
                    : section
            )
        };
        
        addToHistory();
        setLocAssessment(updatedAssessment);
        debouncedAutoSave(updatedAssessment);
    };
    
    const addNewSection = () => {
        const newSectionIndex = Math.max(...Object.keys(locAssessment.questions).map(Number), -1) + 1;
     
        const newSection = [{
            ...newSubHeader(),
            title: {
                "type": "paragraph",
                "content": [
                    {
                        "text": `Section ${newSectionIndex + 1}`,
                        "type": "text",
                        "marks": [
                            {
                                "type": "bold"
                            }
                        ]
                    }
                ]
            }
        }];

        addToHistory();

        const updatedAssessment = {
            ...locAssessment,
            questions: [
                ...locAssessment.questions, newSection
            ]
        };

        setLocAssessment(updatedAssessment);
        debouncedAutoSave(updatedAssessment);

        setCurrentSection(newSectionIndex);
    };

    const handleMove = (sectionKey: number, index: number, direction: number) => {
        const sectionQuestions = locAssessment.questions[sectionKey];

        const temp = sectionQuestions[index];
        sectionQuestions[index] = sectionQuestions[index + direction];
        sectionQuestions[index + direction] = temp;
        
        const updatedAssessment = {
            ...locAssessment,
            questions: locAssessment.questions.map((section, index) =>
                index === currentSection
                    ? sectionQuestions
                    : section
            )
        };
       
        addToHistory();

        setLocAssessment(updatedAssessment);
        debouncedAutoSave(updatedAssessment);

    };
    
    // fix up
    const handleRemove = (sectionKey: number, index: number) => {
        if (sectionKey == 0 && Object.keys(itemRefs.current).length < 2 ) {
            showMessage("There must be at least one section", 1);
            return;
        }

        const itemToRemove = locAssessment.questions[sectionKey][index];
        
        const updatedSection = locAssessment.questions[sectionKey].filter((_, i) => i !== index);

        let updatedQuestions;

        if (updatedSection.length > 0) {
            updatedQuestions = locAssessment.questions.map((section, idx) => 
                idx === sectionKey ? updatedSection : section
            );
        } else {
            updatedQuestions = locAssessment.questions.filter((_, idx) => idx !== sectionKey);
        }

        if (itemRefs.current[itemToRemove.id]) {
            const { [itemToRemove.id]: _, ...restItems } = itemRefs.current;
            itemRefs.current = restItems;
        }
        
        if (currentSection > 0 && updatedSection.length < 1) {
            setCurrentSection(updatedQuestions.length - 1);
        } 

        const updatedAssessment = {
            ...locAssessment,
            questions: updatedQuestions
        }

        addToHistory();
        
        setLocAssessment(updatedAssessment);
        debouncedAutoSave(updatedAssessment);
        
    };
    
    const handleUndo = () => {
        if (historyIndex > 0) {
            isUndoRedoAction.current = true;
            setHistoryIndex(historyIndex - 1);

            const updatedAssessment = JSON.parse(JSON.stringify(history[historyIndex - 1]));
            
            setLocAssessment(updatedAssessment);
            debouncedAutoSave(updatedAssessment);
        }
    };

    const handleRedo = () => {
      
        if (historyIndex < history.length - 1) {
            isUndoRedoAction.current = true;
            setHistoryIndex(historyIndex + 1);

            const updatedAssessment = JSON.parse(JSON.stringify(history[historyIndex + 1]));
            
            setLocAssessment(updatedAssessment);
            debouncedAutoSave(updatedAssessment);
        }
    };
    
    const previewAssessment = () => {
        sessionStorage.setItem('previewAssessment', JSON.stringify(locAssessment));
        
        window.open(`/classroom/${assessment.classroom_id}/assessments/${assessment.id}/preview`, '_blank');
    };

    function isTheoryQuestion(q: Question): q is TheoryQuestion {
        return q.type === 1 || q.type === 2;
    }

    function isObjectiveQuestion(q: Question): q is ObjectiveQuestion {
        return q.type === 3 || q.type === 4;
    }

    useEffect(() => {
        setLocAssessment(assessment);
    }, [assessment]);

    return (
        <Box sx={{ minHeight: '100vh' }}>
            <Box
                sx={{ 
                    margin: 'auto',
                    maxWidth: '1000px',
                    display: "flex", 
                    flexDirection: { xs: "column", md: "row" },
                    position: "relative"
                }}
            >

                <Stack
                    sx={{
                        flex: 1,
                        position: 'relative',
                    }}
                    spacing={2}
                >

                    {locAssessment.questions &&
                        (() => {
                            const sectionQuestions = locAssessment.questions[currentSection] || [];
                            
                            return (
                                <Stack key={currentSection} spacing={2}>
                                    {sectionQuestions.map((question, index) => {
                                        const isFirst = index === 0;
                                        const isLast = index === sectionQuestions.length - 1;

                                        const baseProps = {
                                            sectionKey: currentSection,
                                            setSaved: setSaved,
                                            saveFunction: itemSave,
                                            register: (id: string, getItem: () => Item | TheoryQuestion | ObjectiveQuestion) => {
                                                itemRefs.current[id] = getItem;
                                            },
                                        };

                                    let renderedComponent: React.ReactNode = null;

                                    if (question.type === 0) {
                                        renderedComponent = <SubHeader key={question.id} item={question} {...baseProps} />;
                                    } else if (isTheoryQuestion(question)) {
                                        renderedComponent = (
                                            <TheoryView
                                                key={question.id}
                                                item={question}
                                                {...baseProps}
                                                type={question.type}
                                                auto_marker={assessment.auto_marker}
                                            />
                                        );
                                    } else if (isObjectiveQuestion(question)) {
                                        renderedComponent = (
                                            <ObjectiveView
                                                key={question.id}
                                                item={question}
                                                {...baseProps}
                                                type={question.type}
                                                auto_marker={assessment.auto_marker}
                                            />
                                        );
                                    } else if (question.type === 5) {
                                        renderedComponent = <FileUploadView key={question.id} item={question} {...baseProps} />;
                                    }

                                    if (!renderedComponent) return null;

                                    return (
                                        <Box key={question.id} sx={{ marginBottom: 2 }}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "flex-start",
                                                    mb: 1,
                                                }}
                                            >
                                                <Box />

                                                <Stack
                                                    direction="row"
                                                    spacing={1}
                                                    sx={{
                                                        backgroundColor: "rgba(255, 255, 255, 0.42)",
                                                        marginBottom: -0.5,
                                                        borderRadius: 4,
                                                    }}
                                                >
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleMove(currentSection, index, -1)}
                                                        disabled={isFirst}
                                                    >
                                                        <ArrowUpward fontSize="small" />
                                                    </IconButton>

                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleMove(currentSection, index, 1)}
                                                        disabled={isLast}
                                                    >
                                                        <ArrowDownward fontSize="small" />
                                                    </IconButton>

                                                    <IconButton size="small" onClick={() => handleRemove(currentSection, index)}>
                                                        <Close fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            </Box>

                                            {renderedComponent}
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            );

                        })()
                    }
                </Stack>
            

                <Paper
                    sx={{
                        display:  {xs: 'none', md: "flex"},
                        flexDirection: "column",
                        alignItems: "center",
                        bgcolor: "#f7f2f3",
                        p: 1,
                        borderRadius: 3,
                        boxShadow: 2,
                        minWidth: 48,
                        ml: 2,
                        alignSelf: "flex-end",
                    }}
                >
                    <Tooltip title="Add Item" arrow>
                        <span>
                            <IconButton onClick={handleMenuOpen} color="primary">
                                <Add />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title="Add Section" arrow>
                        <span>
                            <IconButton onClick={addNewSection} color="secondary">
                                <Splitscreen />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title="Undo" arrow>
                        <span>
                            <IconButton onClick={handleUndo} disabled={historyIndex < 1} color="warning">
                                <Undo />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title="Redo" arrow>
                        <span>
                            <IconButton onClick={handleRedo} disabled={historyIndex >= history.length - 1} color="success">
                                <Redo />
                            </IconButton>
                        </span>
                    </Tooltip>

                    <Tooltip title="Preview Assessment" arrow>
                        <span>
                            <IconButton onClick={previewAssessment} color="info">
                                <Preview />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Paper>

                    
                <Paper
                    elevation={3}
                    sx={{
                        position: "sticky",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        bgcolor: "white",
                        display: {xs: "flex", md: "none"},
                        justifyContent: "space-around",
                        p: 1,
                        mt: 2,
                        zIndex: 10,
                    }}
                    >
                        <Tooltip title="Add Item" arrow>
                            <span>
                                <IconButton onClick={handleMenuOpen} color="primary">
                                    <Add />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="Add Section" arrow>
                            <span>
                                <IconButton onClick={addNewSection} color="secondary">
                                    <Splitscreen />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="Undo" arrow>
                            <span>
                                <IconButton onClick={handleUndo} disabled={historyIndex <= 0} color="warning">
                                    <Undo />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="Redo" arrow>
                            <span>
                                <IconButton onClick={handleRedo} disabled={historyIndex >= history.length - 1} color="success">
                                    <Redo />
                                </IconButton>
                            </span>
                        </Tooltip>

                        <Tooltip title="Preview Assessment" arrow>
                            <span>
                                <IconButton onClick={previewAssessment} color="info">
                                    <Preview />
                                </IconButton>
                            </span>
                        </Tooltip>
                </Paper>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: 'auto', maxWidth: '1000px', marginTop: 3 }}>
                {(() => {
                    const sectionKeys = Object.keys(locAssessment.questions || {}).map(Number).sort((a, b) => a - b);
                    const currentIndex = sectionKeys.indexOf(currentSection);
                    
                    return (
                        <>
                            <Button 
                                variant="outlined"
                                startIcon={<NavigateBefore />}
                                onClick={() => {
                                    if (currentIndex > 0) {
                                        setCurrentSection(sectionKeys[currentIndex - 1]);
                                    }
                                }}
                                disabled={!locAssessment.questions || Object.keys(locAssessment.questions).length <= 1 || currentIndex === 0}
                                sx={{
                                    borderColor: "#616263",
                                    color: "#374151",
                                    '&:hover': {
                                        borderColor: "#9ca3af",
                                        backgroundColor: "#f9fafb",
                                    },
                                }}
                            >
                                Previous
                            </Button>
                            
                            
                            <Button 
                                variant="outlined"
                                endIcon={<NavigateNext />}
                                sx={{ 
                                    marginRight: {xs: "", md: 10},
                                    borderColor: "#616263",
                                    color: "#374151",
                                    '&:hover': {
                                        borderColor: "#9ca3af",
                                        backgroundColor: "#f9fafb",
                                    },
                                }}
                                onClick={() => {
                                    if (currentIndex < sectionKeys.length - 1) {
                                        setCurrentSection(sectionKeys[currentIndex + 1]);
                                    }
                                }}
                                disabled={!locAssessment.questions || Object.keys(locAssessment.questions).length <= 1 || currentIndex === sectionKeys.length - 1}
                            >
                                Next
                            </Button>
                        </>
                    );
                })()}
            </Box>

            <Menu
                anchorEl={menuAnchorEl}
                open={isMenuOpen}
                onClose={handleMenuClose}
                PaperProps={{
                    style: {
                        minWidth: 240,
                        borderRadius: 8,
                        padding: '4px 0',
                    },
                    elevation: 3,
                }}
            >
                {menuStructure.map((category, categoryIndex) => (
                    <Box key={category.category}>
                        {categoryIndex > 0 && <Divider />}
            
                        {category.items.map((item) => (
                            <MenuItem
                                key={item.name}
                                onClick={() => {
                                    addNewItem(item.type);
                                    handleMenuClose();
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    pl: 2.5,
                                    fontSize: '0.95rem',
                                }}
                            >
                                {category.icon}
                                {item.name}
                            </MenuItem>
                        ))}
                    </Box>
                ))}
            </Menu>
            
        </Box>
    );
}

export default memo(Items);